import * as React from 'react';
import { useCallback } from 'react';

import { Pressable } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';

import { A11yWrapper } from 'components/a11y-wrapper';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { Box, Typography } from 'components/ui';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { primitive, spacing } from 'styles/constants';
import { isNative } from 'utils/environment';
import { TraceName, stopTrace } from 'utils/performance/trace';

import { StoreOrderButton } from '../store-actions/components';

import { StoreCardActions, StoreCardDetails } from './components';
import { getOrderHereButtonClickSource } from './order-here-button-click-source';
import { StoreCardViewProps } from './types';

export const StoreCardView: React.FC<StoreCardViewProps> = ({
  tab,
  isFavorite = false,
  isUpdatingFavorites = false,
  onCardPress,
  onDiagnosticsClick,
  onFavoriteClick,
  onOrderPress,
  onInfoPress,
  storeDetails,
  errorMessage,
  isInMapView,
  isInSupportForm,
  isSelected,
  isLastItem,
}) => {
  const {
    address,
    distanceText,
    restaurant,
    restaurantStatus: { canBeSelected, text: storeStatusText },
    isHalal,
    city,
    accessInstructions,
  } = storeDetails;
  const { modalAuthState } = useAuthContext();

  const isFavoriteSearch = modalAuthState.screen === ModalAuthScreen.SIGN_UP;
  const halalTitle = isHalal ? t('isHalalTitle') : '';
  const accessibilityLabel = `${address} ${city} ${storeStatusText}, ${halalTitle}, ${accessInstructions}`;

  const handleOnPress = () => {
    if (!isSelected) {
      onCardPress?.();
    }
  };

  const onLayout = useCallback(() => {
    if (tab === OrderLocatorTab.NEARBY) {
      // the 1st store that hits will stop the trace, any more don't matter as trace is done.
      stopTrace(TraceName.STORE_LOCATOR_AUTO_NEARBY_LOCATION_SEARCH_TO_LIST_ITEM_DISPLAYED);
      stopTrace(TraceName.STORE_LOCATOR_RESTAURANT_ALREADY_SELECTED_TO_LIST_ITEM_DISPLAYED);
      stopTrace(TraceName.STORE_LOCATOR_MANUAL_SEARCH_LOCATION_PRESSED_TO_LIST_ITEM_DISPLAYED);
    }
  }, [tab]);

  return (
    <Box
      onLayout={onLayout}
      style={[
        styles.container,
        !canBeSelected && styles.unavailableContainer,
        isNative && isLastItem && styles.lastItemContainer,
      ]}
    >
      <Pressable
        onPress={handleOnPress}
        accessible={!isSelected}
        accessibilityState={{ expanded: !!isSelected }}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        dd-action-name={`${storeDetails.address} open accordion`}
        aria-expanded={isSelected}
      >
        <Box style={styles.row}>
          <StoreCardDetails
            errorMessage={errorMessage}
            storeDetails={storeDetails}
            isInMapView={isInMapView}
            distanceComponent={
              <Box style={styles.distanceWrapper}>
                <A11yWrapper style={styles.distanceA11yWrapper}>
                  <Typography fontFamily="heading" focusable>
                    {distanceText}
                  </Typography>
                </A11yWrapper>
              </Box>
            }
          />
        </Box>
      </Pressable>
      <Box style={styles.row}>
        {(isSelected || isInMapView) && (
          <StoreCardActions
            dd-action-name={`${address} ${isFavorite ? 'unfavorite' : 'favorite'}`}
            isFavorite={isFavorite}
            canBeSelected={canBeSelected}
            onDiagnosticsClick={!isFavoriteSearch ? onDiagnosticsClick : undefined}
            onFavoritesClick={!isFavoriteSearch ? onFavoriteClick : onCardPress}
            onInfoPress={onInfoPress}
            isUpdatingFavorites={isUpdatingFavorites}
            containerStyle={{
              flex: onDiagnosticsClick ? 2 : 1,
              paddingTop: spacing.$7,
              justifyContent: 'space-around',
              paddingRight: spacing.$6,
            }}
          />
        )}
        <Box style={styles.orderButtonWrapper}>
          {isSelected && onInfoPress && !canBeSelected && (
            <ActionButton
              fullWidth
              variant={ActionButtonVariants.OUTLINE}
              onPress={onInfoPress}
              alignSelf={'center'}
              marginTop={'$5'}
              borderColor={primitive.$bbqBrown}
              _text={{ color: primitive.$bbqBrown }}
            >
              {t('viewStoreHours')}
            </ActionButton>
          )}
          {(isSelected || isInMapView) && canBeSelected && (
            <StoreOrderButton
              isInSupportForm={isInSupportForm}
              onOrderPress={onOrderPress}
              orderHereSource={getOrderHereButtonClickSource({ tab, isInSupportForm })}
              restaurant={restaurant}
              disabled={!canBeSelected}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    padding: spacing.$4,
  },
  distanceA11yWrapper: {
    flexGrow: 0,
  },
  distanceWrapper: {
    alignSelf: 'flex-end',
  },
  lastItemContainer: {
    paddingBottom: spacing.$7,
  },
  orderButtonWrapper: {
    flex: 5,
  },
  row: {
    flexDirection: 'row',
  },
  unavailableContainer: {
    backgroundColor: primitive.$blackOpacity4,
  },
});
